import { Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { fontWeights } from '@/@mui/tokens/font-weights';

interface ColorBoxProps {
  color: string;
  name: string;
  dark?: boolean;
  light?: boolean;
}

const ColorBox = ({ color, name, dark, light }: ColorBoxProps) => {
  return (
    <Stack
      bgcolor={color}
      border={light ? '1px solid' : 'none'}
      borderColor={light ? 'neutral.100' : 'transparent'}
      borderRadius={1}
      px={1.5}
      py={1}
    >
      <Typography color={dark ? 'white' : 'black'}>{name}</Typography>
      <Typography variant="body2" color={dark ? 'white' : 'black'}>
        {color}
      </Typography>
    </Stack>
  );
};

export const Colors = () => {
  const theme = useTheme();

  return (
    <Stack gap={2}>
      <Typography variant="h2">Colors</Typography>
      <Box>
        <Typography fontWeight={fontWeights.fontWeightMedium} mb={1}>
          Neutral
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          <ColorBox color={theme.palette.neutral.lighter} name="lighter" />
          <ColorBox color={theme.palette.neutral.light} name="light" />
          <ColorBox color={theme.palette.neutral.main} name="main" dark />
          <ColorBox color={theme.palette.neutral.dark} name="dark" dark />
          <ColorBox color={theme.palette.neutral.darker} name="darker" dark />
        </Stack>
      </Box>
      <Box>
        <Typography fontWeight={fontWeights.fontWeightMedium} mb={1}>
          Primary
        </Typography>
        <Stack direction="row" gap={1}>
          <ColorBox color={theme.palette.primary.light} name="light" />
          <ColorBox color={theme.palette.primary.main} name="main" dark />
          <ColorBox color={theme.palette.primary.dark} name="dark" dark />
        </Stack>
      </Box>
      <Box>
        <Typography fontWeight={fontWeights.fontWeightMedium} mb={1}>
          Secondary
        </Typography>
        <Stack direction="row" gap={1}>
          <ColorBox color={theme.palette.secondary.light} name="light" />
          <ColorBox color={theme.palette.secondary.main} name="main" />
          <ColorBox color={theme.palette.secondary.dark} name="dark" />
        </Stack>
      </Box>
      <Box>
        <Typography fontWeight={fontWeights.fontWeightMedium} mb={1}>
          Error
        </Typography>
        <Stack direction="row" gap={1}>
          <ColorBox color={theme.palette.error.light} name="light" />
          <ColorBox color={theme.palette.error.main} name="main" dark />
          <ColorBox color={theme.palette.error.dark} name="dark" dark />
        </Stack>
      </Box>
      <Box>
        <Typography fontWeight={fontWeights.fontWeightMedium} mb={1}>
          Success
        </Typography>
        <Stack direction="row" gap={1}>
          <ColorBox color={theme.palette.success.light} name="light" />
          <ColorBox color={theme.palette.success.main} name="main" dark />
          <ColorBox color={theme.palette.success.dark} name="dark" dark />
        </Stack>
      </Box>
      <Box>
        <Typography fontWeight={fontWeights.fontWeightMedium} mb={1}>
          Warning
        </Typography>
        <Stack direction="row" gap={1}>
          <ColorBox color={theme.palette.warning.light} name="light" />
          <ColorBox color={theme.palette.warning.main} name="main" />
          <ColorBox color={theme.palette.warning.dark} name="dark" dark />
        </Stack>
      </Box>
      <Box>
        <Typography fontWeight={fontWeights.fontWeightMedium} mb={1}>
          Info
        </Typography>
        <Stack direction="row" gap={1}>
          <ColorBox color={theme.palette.info.light} name="light" />
          <ColorBox color={theme.palette.info.main} name="main" dark />
          <ColorBox color={theme.palette.info.dark} name="dark" dark />
        </Stack>
      </Box>
      <Box>
        <Typography fontWeight={fontWeights.fontWeightMedium} mb={1}>
          Status
        </Typography>
        <Stack direction="row" gap={1}>
          {Object.entries(theme.palette.status).map(([key, value]) => (
            <ColorBox
              key={key}
              color={String(value)}
              name={key}
              dark={key === 'progress'}
            />
          ))}
        </Stack>
      </Box>
    </Stack>
  );
};
